import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { IAutocompleteDataRow } from 'src/app/shared/interfaces/autocomplete-data-row.interface';
import { IPharmacy } from 'src/app/shared/interfaces/pharmacy.interface';
import { GetBuyersList, GetEdiSuppliersList, GetPartyLatestDetails, GetSelectedPartyLatestDetailsByID as GetSelectedPartyLatestDetailsByID} from 'src/app/shared/states/orders/orders.actions';
import { OrdersState } from 'src/app/shared/states/orders/orders.state';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { OrderPartyDetail, OrderPartyTypeEnum } from '../../interfaces/order-party-details.interface';
import { BuyerSupplierService } from '../../services/buyer-supplier.service';

@UntilDestroy()
@Component({
  selector: 'app-supplier-buyer-sale-details',
  templateUrl: './supplier-buyer-sale-details.component.html',
  styleUrls: ['./supplier-buyer-sale-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SupplierBuyerSaleDetailsComponent implements OnInit {

  @Input() supplier: OrderPartyDetail;
  @Input() buyer: OrderPartyDetail;
  @Input() readOnly: boolean;
  @Input() takeDefaultSupplier: boolean = true;
  @Input() pharmacyIsSupplier: boolean = true;

  @Output() currentBuyerDetailId: EventEmitter<string> = new EventEmitter();
  @Output() currentSupplierDetailId: EventEmitter<string> = new EventEmitter();
  @Output() currentBuyerChanged: EventEmitter<OrderPartyDetail> = new EventEmitter();
  @Output() currentSupplierChanged: EventEmitter<OrderPartyDetail> = new EventEmitter();

  @Select(OrdersState.orderPartyDetails) buyerDetails$: Observable<OrderPartyDetail>;
  
  OrderPartyTypeEnum = OrderPartyTypeEnum;
  buyers: IAutocompleteDataRow[] = [];
  ediSuppliers: IAutocompleteDataRow[] = [];

  @ViewChild('supplierEl') supplierEl;
  @ViewChild('buyerEl') buyerEl;

  /**
   *
   */
  constructor(private store: Store, private buyerSupplierService: BuyerSupplierService) {
    
  }
  ngOnInit(): void {
    this.setUpSupplierForSellingPharmacy();

    this.setUpBuyerForBuyingPharmacy();
   
    this.getBuyersIfPharmacyIsSeller();
    
    this.getEdiSuppliersIfPharmacyIsBuyer();

    this.store.select(OrdersState.buyers)
    .pipe(untilDestroyed(this))
    .subscribe(buyers => {
      this.buyers = buyers?.map(x => ({id: x.id, name: x.companyName}));
    });

    this.store.select(OrdersState.ediSuppliers)
    .pipe(untilDestroyed(this))
    .subscribe(ediSuppliers => {
      this.ediSuppliers = ediSuppliers?.map(x => ({id: x.id, name: x.companyName}));
    });
  }

  private setUpSupplierForSellingPharmacy(): void {
    if (!this.readOnly && !this.supplier && this.pharmacyIsSupplier) {
      //get latest pharmacy supplier details
      this.store.dispatch(new GetPartyLatestDetails(OrderPartyTypeEnum.Supplier))
      .pipe(take(1))
      .subscribe(() => {
        const supplier = this.store.selectSnapshot(OrdersState.latestSupplierOrEdiBuyerDetails);
        if (supplier) {
          this.supplier = {...supplier};
          this.currentSupplierDetailId.next(this.supplier.id);
          this.currentSupplierChanged.next(this.supplier);
        }
        else  {
          this.buyerSupplierService.createDefaultSupplierOrEdiBuyer()
          .then((supplier) => {
            this.supplier = supplier;
            this.currentSupplierDetailId.next(this.supplier.id);
            this.currentSupplierChanged.next(this.supplier);
          });
        }
      })
    }

    if (!this.supplier && this.readOnly && this.takeDefaultSupplier && this.pharmacyIsSupplier) {
      this.setDefaultSupplier();
    }
  }

  private setUpBuyerForBuyingPharmacy(): void {
    if (!this.readOnly && !this.buyer && !this.pharmacyIsSupplier) {
      const buyerHandler = (buyer: OrderPartyDetail) => {
        this.buyer = {...buyer};
        this.currentBuyerDetailId.next(this.buyer.id);
        this.currentBuyerChanged.next(this.buyer);
      }
      //get latest pharmacy edi buyer details
      this.store.dispatch(new GetPartyLatestDetails(OrderPartyTypeEnum.Supplier))
      .pipe(take(1))
      .subscribe(() => {
        const buyer = this.store.selectSnapshot(OrdersState.latestSupplierOrEdiBuyerDetails);
        if (buyer) {
          buyerHandler(buyer);
        }
        else  {
          this.buyerSupplierService.createDefaultSupplierOrEdiBuyer(OrderPartyTypeEnum.Supplier)
          .then(buyerHandler);
        }
      })
    }
  }
  

  private getBuyersIfPharmacyIsSeller(): void {
    if(!this.store.selectSnapshot(OrdersState.buyers)?.length && !this.readOnly && this.pharmacyIsSupplier) {
      this.store.dispatch(new GetBuyersList());
    }
  }

  private getEdiSuppliersIfPharmacyIsBuyer(): void {
    if(!this.store.selectSnapshot(OrdersState.ediSuppliers)?.length && !this.readOnly && !this.pharmacyIsSupplier) {
      this.store.dispatch(new GetEdiSuppliersList());
    }
  }


  buyerAddedModified(buyer: OrderPartyDetail): void {
    this.buyer = buyer;
    this.currentBuyerDetailId.next(buyer.id);
    this.currentBuyerChanged.next(this.buyer);
  }

  supplierAddedModified(supplier: OrderPartyDetail): void {
    this.supplier = supplier;
    this.currentSupplierDetailId.next(supplier.id);
    this.currentSupplierChanged.next(this.supplier);
  }

  buyerSelected(event: IAutocompleteDataRow): void {
     if (event?.id) {
      this.store.dispatch(new GetSelectedPartyLatestDetailsByID(event.id))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const loadedDetails = this.store.selectSnapshot(OrdersState.orderPartyDetails);
        this.buyer = loadedDetails;
        this.currentBuyerDetailId.next(loadedDetails?.id);
        this.currentBuyerChanged.next(this.buyer);
      });
    }
  }

  buyerRemoved(): void {
    this.buyer = null;
    this.currentBuyerDetailId.next(null);
    this.currentBuyerChanged.next(this.buyer);
  }

  ediSupplierSelected(event: IAutocompleteDataRow): void {
    if (event?.id) {
      this.store.dispatch(new GetSelectedPartyLatestDetailsByID(event.id))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const loadedDetails = this.store.selectSnapshot(OrdersState.orderPartyDetails);
        this.supplier = loadedDetails;
        this.currentSupplierDetailId.next(loadedDetails?.id);
        this.currentSupplierChanged.next(this.supplier);
      });
    }
  }

  ediSupplierRemoved(): void {
    this.supplier = null;
    this.currentSupplierDetailId.next(null);
    this.currentSupplierChanged.next(this.supplier);
  }

  
  focusInput(): void {
    if (!this.supplier) {
      this.supplierEl.focus();
    } else if (!this.buyer) {
      this.buyerEl.focus();
    }
  }

  highlightRequiredControlsWithErrors(): void {
    this.supplierEl.highlightRequiredControlsWithErrors();
    this.buyerEl.highlightRequiredControlsWithErrors();
  }

  private setDefaultSupplier(): void {
    const currentPharmacy = this.store.selectSnapshot(PharmacyState.currentPharmacy) as IPharmacy;
    this.supplier = {
      deaNumber: currentPharmacy.deaNumber,
      physicalAddress: {
        address1: currentPharmacy.street,
        city: currentPharmacy.city,
        state: currentPharmacy.state,
        zipCode: currentPharmacy.zipCode
      },
      shippingAddress: {
        address1: currentPharmacy.street,
        city: currentPharmacy.city,
        state: currentPharmacy.state,
        zipCode: currentPharmacy.zipCode
      },
      orderParty: {
        pharmacyID: currentPharmacy.id,
        companyName: currentPharmacy.name,
        type: OrderPartyTypeEnum.Supplier
      }
    }
    
  }
}
